import React, { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { ModuleLayout } from '../../../components/layout/ModuleLayout';

import { Workflow } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useOperationalContext } from '../../../contexts/OperationalContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useExecutionFiltersSlot, useExecutionRibbonState } from '../context/ExecutionModuleContext';
import { GestaoDashboardPage } from '../../gestao/pages';
import { ExecutionDashboardPage } from './ExecutionDashboard';
import { ExecutionInstancePage } from './ExecutionInstancePage';
import { WorkflowsRibbon } from '../components/WorkflowsRibbon';

const NOOP = () => {};

function useWorkflowsRibbonElement() {
  const ribbonState = useExecutionRibbonState();
  const filtersSlot = useExecutionFiltersSlot();
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo(() => (
    <WorkflowsRibbon
      // Ações principais
      onNew={ribbonState?.create?.onClick ?? NOOP}
      canNew={ribbonState?.create?.enabled ?? false}
      newTooltip={ribbonState?.create?.tooltip}
      pauseLabel={ribbonState?.instance?.pauseLabel ?? 'Pausar'}
      onPause={ribbonState?.instance?.onPauseOrResume ?? NOOP}
      canPause={ribbonState?.instance?.canPauseOrResume ?? false}
      onCancel={ribbonState?.instance?.onCancel ?? NOOP}
      canCancel={ribbonState?.instance?.canCancel ?? false}
      onRefresh={ribbonState?.refresh?.onRefresh ?? NOOP}
      isRefreshing={ribbonState?.refresh?.isRefreshing ?? false}
      instanceBusy={ribbonState?.instance?.busy ?? false}
      // Execução
      onStart={ribbonState?.stage?.onStart ?? NOOP}
      canStart={ribbonState?.stage?.canStart ?? false}
      onAdvance={ribbonState?.stage?.onOpenAction ?? NOOP}
      canAdvance={ribbonState?.stage?.canOpenAction ?? false}
      onComplete={ribbonState?.stage?.onComplete ?? NOOP}
      canComplete={ribbonState?.stage?.canComplete ?? false}
      // Utilidades
      onBlock={ribbonState?.stage?.onBlock ?? NOOP}
      canBlock={ribbonState?.stage?.canBlock ?? false}
      onReprocess={ribbonState?.instance?.onReprocess ?? NOOP}
      canReprocess={ribbonState?.instance?.canReprocess ?? false}
      onExport={ribbonState?.export?.onExport ?? NOOP}
      canExport={ribbonState?.export?.canExport ?? false}
      // Navegação
      onViewInstances={() => navigate('/execucoes/instancias')}
      instancesActive={location.pathname.includes('/instancias')}
      onViewKanban={() => navigate('/execucoes/kanban')}
      kanbanActive={location.pathname.includes('/kanban')}
      // Filtros
      filtersSlot={filtersSlot}
    />
  ), [ribbonState, filtersSlot, navigate, location.pathname]);
}

export const ExecutionHubPage: React.FC = () => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { setOperationalMetrics } = useOperationalContext();
  const ribbonElement = useWorkflowsRibbonElement();

  useEffect(() => {
    setOperationalMetrics({
      globalSla: 0,
      throughput7d: 0,
      userWip: 0,
      bottleneckStage: null,
    });
  }, [setOperationalMetrics]);

  return (
    <ModuleLayout ribbon={ribbonElement}>
      <div className="flex h-full flex-col overflow-hidden">

        <div className="min-h-0 flex-1">
          <Routes>
            <Route index element={<Navigate to="instancias" replace />} />
            <Route
              path="instancias"
              element={
                <ExecutionDashboardPage />
              }
            />
            <Route path="minhas-tarefas" element={<Navigate to="/minha-mesa" replace />} />
            <Route path="minhas-tarefas/*" element={<Navigate to="/minha-mesa" replace />} />
            <Route path="kanban" element={<GestaoDashboardPage compactMode />} />
            <Route path="analytics" element={<Navigate to="/operacoes" replace />} />
            <Route path="auditoria" element={<Navigate to="/execucoes/instancias" replace />} />
            <Route path="*" element={<Navigate to="instancias" replace />} />
          </Routes>
        </div>
      </div>
    </ModuleLayout>
  );
};

export const ExecutionInstanceModulePage: React.FC = () => {
  const ribbonElement = useWorkflowsRibbonElement();

  return (
    <ModuleLayout ribbon={ribbonElement}>
      <ExecutionInstancePage />
    </ModuleLayout>
  );
};
